<script setup lang="ts">
import { mdiArrowLeft } from '@mdi/js'
// DESIGN.md section 9.1, screen 5 sub-screen — Versus. Pick two players, see head-to-head per
// pool plus recent matches between them.
import { computed, ref, watch } from 'vue'
import Avatar from '../components/Avatar.vue'
import PlayerSearchList from '../components/PlayerSearchList.vue'
import { getVersus } from '../backend'
import { navigate } from '../store/nav'
import type { Player, RatingPool, VersusResponse } from '../types'

const playerA = ref<Player | null>(null)
const playerB = ref<Player | null>(null)
const result = ref<VersusResponse | null>(null)
const loadError = ref(false)
const pickerFor = ref<'a' | 'b' | null>(null)

const pools: RatingPool[] = ['rapid', 'classical']

async function loadResult() {
  if (!playerA.value || !playerB.value) {
    result.value = null
    return
  }
  loadError.value = false
  try {
    result.value = await getVersus(playerA.value.id, playerB.value.id)
  } catch {
    loadError.value = true
  }
}

watch([playerA, playerB], loadResult)

const ready = computed(() => !!playerA.value && !!playerB.value)
const sameSelected = computed(() => !!playerA.value && !!playerB.value && playerA.value.id === playerB.value.id)

function pick(player: Player) {
  if (pickerFor.value === 'a') playerA.value = player
  if (pickerFor.value === 'b') playerB.value = player
  pickerFor.value = null
}

function poolLine(pool: RatingPool) {
  const p = result.value?.pools[pool]
  if (!p || p.matches === 0) return 'No matches in this pool'
  return `${p.matches} match${p.matches === 1 ? '' : 'es'} · ${p.winsA}-${p.winsB} · games ${p.gamesA}-${p.gamesB} · points ${p.pointsA}-${p.pointsB}`
}

function openMatch(matchId: number) {
  navigate('pastMatchDetail', { matchId })
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col p-4 gap-4">
    <div class="flex items-center gap-3">
      <button
        type="button"
        class="tap-target w-10 h-10 min-h-0 min-w-0 shrink-0 rounded-full active:bg-neutral-800 flex items-center justify-center text-neutral-400"
        aria-label="Back to players"
        @click="navigate('players')"
      >
        <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor"><path :d="mdiArrowLeft" /></svg>
      </button>
      <h1 class="text-xl font-bold flex-1">Versus</h1>
    </div>

    <div class="flex items-center justify-center gap-8">
      <button type="button" class="tap-target flex flex-col items-center gap-2 rounded-2xl bg-neutral-900 border border-neutral-800 px-6 py-4 active:bg-neutral-800" @click="pickerFor = 'a'">
        <Avatar :player="playerA" :size="64" />
        <span class="font-semibold">{{ playerA?.displayName ?? 'Pick player A' }}</span>
      </button>
      <span class="text-3xl font-black text-neutral-600">VS</span>
      <button type="button" class="tap-target flex flex-col items-center gap-2 rounded-2xl bg-neutral-900 border border-neutral-800 px-6 py-4 active:bg-neutral-800" @click="pickerFor = 'b'">
        <Avatar :player="playerB" :size="64" />
        <span class="font-semibold">{{ playerB?.displayName ?? 'Pick player B' }}</span>
      </button>
    </div>

    <p v-if="sameSelected" class="text-center text-red-400 font-semibold">Pick two different players</p>
    <p v-if="loadError" class="text-center text-neutral-500">Could not load head-to-head</p>

    <template v-if="ready && !sameSelected && result">
      <div class="flex flex-col gap-2">
        <div v-for="pool in pools" :key="pool" class="rounded-xl bg-neutral-900 border border-neutral-800 px-4 py-3">
          <span class="text-sm font-bold text-neutral-400 uppercase tracking-wide">{{ pool === 'rapid' ? 'Rapid' : 'Classic' }}</span>
          <p class="font-mono">{{ poolLine(pool) }}</p>
        </div>
      </div>

      <div class="flex-1 min-h-0 flex flex-col gap-2 overflow-y-auto">
        <h2 class="text-sm font-bold text-neutral-400 uppercase tracking-wide">Recent matches</h2>
        <button
          v-for="m in result.recent"
          :key="m.id"
          type="button"
          class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-900 border border-neutral-800 px-3 py-2 text-left active:bg-neutral-800"
          @click="openMatch(m.id)"
        >
          <span class="font-mono text-sm flex-1">{{ m.players.left.displayName }} {{ m.gamesWon[m.players.left.id] ?? 0 }}–{{ m.gamesWon[m.players.right.id] ?? 0 }} {{ m.players.right.displayName }}</span>
          <span class="text-xs text-neutral-500">{{ m.matchType.name }}</span>
        </button>
        <div v-if="result.recent.length === 0" class="text-neutral-500">No matches between them yet</div>
      </div>
    </template>

    <PlayerSearchList v-if="pickerFor" @pick="pick" @close="pickerFor = null" />
  </div>
</template>
